import React, { useState, useEffect, useRef } from 'react';

const QuestionCard = ({ 
  question, 
  status, 
  onEdit, 
  onDelete, 
  onTagClick,
  isSelected = false, 
  onToggleSelect,
  isDragging = false,
  showCheckbox = true,
  showActions = true,
  className = ""
}) => {
  const isOnAir = status === 'on_air';
  const [showMenu, setShowMenu] = useState(false);
  const isHighlighted = question.highlight;
  const menuRef = useRef(null);
  
  // Add animation class for highlighting
  const highlightClass = isHighlighted 
    ? 'animate-highlight border-yellow-400 bg-yellow-50' 
    : 'border-gray-300';
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
  
  const toggleMenu = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit && onEdit(question);
  };
  
  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete && onDelete(question.id);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onToggleSelect && onToggleSelect(question.id);
  };

  const handleManageTags = (e) => {
    e.stopPropagation();
    setShowMenu(false);

    if (typeof window !== 'undefined' && window.tagQuestionHandler) {
      window.tagQuestionHandler(question.id);
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return '';
    }
  };

  // Determine background color - use subtle gray for better visibility, but keep red for on-air
  const bgColor = isOnAir ? 'bg-red-50' : 'bg-gray-50';
  
  return (
        <div
          className={`${bgColor} border border-gray-200 rounded-lg p-4 shadow-sm transition-all duration-200 relative
            ${isOnAir ? 'border-red-500 shadow-red-200 shadow-lg ring-2 ring-red-200' : highlightClass}
            ${isHighlighted ? 'shadow-md' : ''}
            ${isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''}
            ${isDragging ? 'opacity-90 shadow-lg' : ''}
            ${className}`}
        >
      {/* Selection Checkbox */}
      {showCheckbox && (
        <div className="absolute top-3 left-3 z-20">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxClick}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
            style={{pointerEvents: 'auto'}}
          />
        </div>
      )}

      <div className={`flex items-start justify-between ${showCheckbox ? 'pl-6' : ''}`}>
        <div className="flex-1 min-w-0">
          {/* ON AIR Badge */}
          {isOnAir && (
            <div className="flex items-center mb-2">
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full mr-2 animate-pulse">
                LIVE
              </span>
              <span className="text-sm text-red-600 font-medium">On Air</span>
            </div>
          )}

          {/* Question Content */}
          <p className="text-gray-900 break-words whitespace-pre-wrap overflow-wrap-anywhere hyphens-auto mb-3" 
             style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {question.content}
          </p>
          
          {/* Tags */}
          {question.tags && question.tags.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1">
              {question.tags.map(tag => (
                <button
                  key={tag.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagClick && onTagClick(tag.id);
                  }}
                  className="px-2 py-0.5 rounded-full text-xs font-medium text-white hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: tag.color, pointerEvents: 'auto' }}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}

          {/* Footer with Date/Time and Submitter */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-2">
              {/* Time */}
              {(question.created_at || question.createdAt) && (
                <div className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">
                    {formatTime(question.created_at || question.createdAt) || '--'}
                  </span>
                </div>
              )}
              
              {/* Submitter */}
              {question.submitterName && (
                <>
                  {(question.created_at || question.createdAt) && <span>•</span>}
                  <span className="italic">From: {question.submitterName}</span>
                </>
              )}
            </div>

            {/* Vote Count (if available) */}
            {question.votes !== undefined && question.votes > 0 && (
              <div className="flex items-center text-gray-500">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span className="font-medium">{question.votes}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {showActions && (
          <div className="flex items-center space-x-1 relative z-30 ml-2">

            {/* More options menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={toggleMenu}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="More Options"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-32">
                  <button
                    onClick={handleEdit}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 rounded-t-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={handleManageTags}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Manage Tags
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;
